let notificationAudio: HTMLAudioElement | null = null;
let notificationContext: AudioContext | null = null;
let notificationBufferPromise: Promise<AudioBuffer> | null = null;

const NOTIFICATION_VOLUME = 0.05;
const NOTIFICATION_SOUND_URL = `/notification_sound.mp3`;

const playedIds = new Set<string>();

type WebkitWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

function getAudioContextClass():
  | typeof AudioContext
  | undefined {
  return (
    window.AudioContext ??
    (window as WebkitWindow).webkitAudioContext
  );
}

async function getOrCreateNotificationContext(): Promise<AudioContext> {
  const AudioContextClass = getAudioContextClass();
  if (!AudioContextClass) {
    throw new Error(`Web Audio API not supported`);
  }

  if (!notificationContext) {
    notificationContext = new AudioContextClass();
  }

  if (notificationContext.state === `suspended`) {
    await notificationContext.resume();
  }

  return notificationContext;
}

async function getNotificationBuffer(): Promise<AudioBuffer> {
  const context = await getOrCreateNotificationContext();

  if (!notificationBufferPromise) {
    notificationBufferPromise = fetch(NOTIFICATION_SOUND_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch notification sound`);
        }
        return response.arrayBuffer();
      })
      .then((arrayBuffer) => context.decodeAudioData(arrayBuffer.slice(0)));
  }

  return notificationBufferPromise;
}

function playWithHtmlAudioFallback(): void {
  if (!notificationAudio) {
    notificationAudio = new Audio(NOTIFICATION_SOUND_URL);
  }
  notificationAudio.volume = NOTIFICATION_VOLUME;
  notificationAudio.currentTime = 0;
  void notificationAudio.play().catch(() => {});
}

export function primePaymentNotificationSound(): void {
  // Call this from a user gesture (tap/click) to unlock Safari/iOS audio.
  void getOrCreateNotificationContext().catch(() => {});
}

export function playPaymentReceivedNotificationSound(dedupeKey?: string): void {
  if (dedupeKey) {
    if (playedIds.has(dedupeKey)) {
      return;
    }
    playedIds.add(dedupeKey);
  }

  try {
    void (async () => {
      try {
        const context = await getOrCreateNotificationContext();
        const buffer = await getNotificationBuffer();

        const source = context.createBufferSource();
        source.buffer = buffer;

        const gain = context.createGain();
        gain.gain.value = NOTIFICATION_VOLUME;

        source.connect(gain);
        gain.connect(context.destination);
        source.start(0);
      } catch {
        playWithHtmlAudioFallback();
      }
    })();
  } catch {
    // Ignore missing Audio API or load failures
  }
}